import React, { useState, useCallback, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import DataTable from "ui/DataTable"
import AddEquipmentModal from "pagesV2/pro/Equipment/AddEquipmentModal"
import { fetchBoatById, cleanBoat } from "store/boats"
import { useDispatch, useSelector } from "react-redux"
import { routes } from "router/routes"
import { useTranslation } from "react-i18next"
import { createEquipmentColumns } from "./columns"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { DSIcon } from "ui/Icon/DSIcon"
import styles from "./index.module.css"

// Example data, replace with your real data source
export const dataSource = []

const EquipmentTable = ({ data = dataSource, onChange, onDeactivate, onDelete, onActivate }) => {
  const [showModal, setShowModal] = useState(false)
  const [boatId, setBoatId] = useState(null)
  const [isDuplicateMode, setIsDuplicateMode] = useState(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const boat = useSelector((state) => state.boats.boat)
  const boatLoading = boat.loading

  useEffect(() => {
    // Fetch boat data whenever modal opens (showModal changes) or boatId changes
    // This ensures we always get fresh data from the API, even when reopening the same equipment
    if (boatId && showModal) {
      dispatch(fetchBoatById(boatId))
    }
  }, [boatId, showModal, dispatch])

  const handleEdit = useCallback((record) => {
    setIsDuplicateMode(false)
    setShowModal(true)
    setBoatId(record.key)
  }, [])

  const handleDuplicate = useCallback((record) => {
    setIsDuplicateMode(true)
    setShowModal(true)
    setBoatId(record.key)
  }, [])

  const handleTitleClick = useCallback((record) => {
    navigate(routes.guideEquipmentDetailsPath({ id: record.key }))
  }, [navigate])

  const handleCancelModal = useCallback(() => {
    setShowModal(false)
    // Clear boat data to prevent showing stale data when reopening
    dispatch(cleanBoat())
  }, [dispatch])

  const handleSave = useCallback(() => {
    setShowModal(false)
    // Clear boat data after save
    dispatch(cleanBoat())
  }, [dispatch])

  const columns = useMemo(() => createEquipmentColumns(handleEdit, onDeactivate, onDelete, onActivate, handleDuplicate, handleTitleClick, t), [handleEdit, onDeactivate, onDelete, onActivate, handleDuplicate, handleTitleClick, t])

  return (
    <section className={styles.equipmentTable}>
      <WithEmptyStateWrapper
        name="equipment"
        hasItems={data?.length > 0}
        fallbackTitle={t("equipment.empty_state.title", { defaultValue: "No equipment added yet" })}
        fallbackDescription={t("equipment.empty_state.description", { defaultValue: "Add your first equipment to get started" })}
        fallbackImageSrc={<DSIcon name="boat" width={80} height={80} />}
      >
        <DataTable columns={columns} dataSource={data} pagination={false} onChange={onChange} />
      </WithEmptyStateWrapper>
      {showModal && (
        <AddEquipmentModal
          visible={showModal}
          onCancel={handleCancelModal}
          onSave={handleSave}
          record={boat}
          boatId={boatId}
          boatLoading={boatLoading}
          isDuplicateMode={isDuplicateMode}
        />
      )}
    </section>
  )
}

EquipmentTable.propTypes = {
  data: PropTypes.array.isRequired,
  onChange: PropTypes.func,
  onDeactivate: PropTypes.func,
  onDelete: PropTypes.func,
  onActivate: PropTypes.func
}

export default React.memo(EquipmentTable)
